import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import CongregationStudentBoardTable from '@/tables/CongregationStudentBoardTable';
import { useCoordinatorAccess } from '~/hooks/useIsCoordinator';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/student-board/',
)({
  component: StudentBoardRoute,
});

/**
 * The group's Student Board tab, gated exactly as legacy registers it:
 * `groupTypes = [congregation, custom-study-group]` and
 * `groupMemberships = [admin, mod]`
 * (`modules/group-student-board/class-group-student-board.php:11-17`). Both
 * axes are `useIsCoordinator`, which is the same predicate the server now runs
 * for `group_id` on `GET /v3/adoption/student-board`.
 *
 * Legacy HIDES the tab, it does not 403 — so a refused caller is redirected to
 * the group dashboard, exactly as `StudentsLayout` does
 * (`$groupName.students.tsx:39-58`).
 *
 * No platform-admin arm, despite the server's `adminCanView` bypass: the board
 * endpoint takes `CurrentStudyHelperDependency`, which refuses any non-study-
 * helper, so a link for an administrator would paint a permanently empty board.
 *
 * `isResolved` is not optional: `getGroupBySlug` is pending on first paint, and
 * a bare `!isCoordinator` bounces the group's own admin before it lands.
 */
function StudentBoardRoute(): ReactElement | null {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const navigate = useNavigate();
  const { isCoordinator, isResolved } = useCoordinatorAccess();
  const refused = isResolved && !isCoordinator;

  useEffect(() => {
    if (refused) {
      navigate({
        to: '/groups/$groupName/',
        params: { groupName },
        replace: true,
      });
    }
  }, [refused, groupName, navigate]);

  if (refused) return null;

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion title={t('lessons.student-board')} />
      </GroupHeaderWrapper>
      <CongregationStudentBoardTable />
    </>
  );
}
